import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import './CallToActionSection.css';

const MobileCallToActionSection = ({
  component: Component = 'section',
  title,
  caption,
  backgroundColor,
  backgroundImage,
  headerTag: HeaderTag = 'h1',
  headerClassName,
  buttons,
  secondaryContents,
  className,
  borderBottom = false,
  text,
}) => {
  const backgroundStyle = backgroundImage ? { backgroundImage } : {};
  const classes = cx(
    'ctaSection--mobile',
    { 'ctaSection--mobile-border-bottom': borderBottom },
    { 'ctaSection--light-blue-bg': backgroundColor === 'lightBlue' },
    { 'ctaSection--soft-pink-bg': backgroundColor === 'softPink' },
    { 'ctaSection--light-gray-bg': backgroundColor === 'lightGray' },
    { 'ctaSection--baby-blue-bg': backgroundColor === 'babyBlue' },
    { 'ctaSection--registry-light-blue-bg': backgroundColor === 'registryLightBlue' },
    { 'ctaSection--light-green-bg': backgroundColor === 'lightGreen' },
    { 'ctaSection--humming-bird-bg': backgroundColor === 'hummingBird' },
    className
  );
  const headerClasses = cx('ctaSection__title', `zui__${HeaderTag}`);
  const captionClasses = cx('body-copy', 'body-copy--align-center', headerClassName);
  return (
    <Component style={backgroundStyle} className={classes}>
      <HeaderTag className={headerClasses}>{title}</HeaderTag>
      <div className="ctaSection__body-content--mobile">
        {secondaryContents}
        <div className={captionClasses}>{caption}</div>
        {text}
        {buttons}
      </div>
    </Component>
  );
};

MobileCallToActionSection.propTypes = {
  className: PropTypes.string,
  headerTag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  backgroundColor: PropTypes.oneOf([
    '',
    'white',
    'lightBlue',
    'softPink',
    'lightGray',
    'babyBlue',
    'registryLightBlue',
    'lightGreen',
    'hummingBird',
  ]),
  backgroundImage: PropTypes.string,
  component: PropTypes.oneOf(['div', 'section']),
  buttons: PropTypes.node,
  secondaryContents: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  caption: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  borderBottom: PropTypes.bool,
  headerClassName: PropTypes.string,
  text: PropTypes.node,
};

const TabletCallToActionSection = ({
  component: Component = 'section',
  title,
  className,
  headerClassName,
  secondaryContents,
  caption,
  backgroundColor,
  backgroundImage,
  headerTag: HeaderTag = 'h1',
  borderBottom = false,
  buttons,
  text,
  heroTitleAlwaysOnTop,
}) => {
  const classes = cx(
    'ctaSection--tablet',
    { 'ctaSection--mobile-border-bottom': borderBottom },
    { 'ctaSection--light-blue-bg': backgroundColor === 'lightBlue' },
    { 'ctaSection--soft-pink-bg': backgroundColor === 'softPink' },
    { 'ctaSection--light-gray-bg': backgroundColor === 'lightGray' },
    { 'ctaSection--baby-blue-bg': backgroundColor === 'babyBlue' },
    { 'ctaSection--registry-light-blue-bg': backgroundColor === 'registryLightBlue' },
    { 'ctaSection--light-green-bg': backgroundColor === 'lightGreen' },
    { 'ctaSection--humming-bird-bg': backgroundColor === 'hummingBird' },
    className
  );
  const headerClasses = cx('ctaSection__title', `zui__${HeaderTag}`, headerClassName);
  const copyClasses = cx('body-copy');
  const buttonClasses = cx('ctaSection--tablet__buttons', 'ctaSection--tablet__buttons--center');
  const backgroundStyle = backgroundImage ? { backgroundImage } : {};

  return (
    <Component style={backgroundStyle} className={classes}>
      {heroTitleAlwaysOnTop && (
        <>
          <HeaderTag className={headerClasses}>{title}</HeaderTag>
          {caption && <div className={copyClasses}>{caption}</div>}
          <div className="ctaSection__secondary-contents">{secondaryContents}</div>
        </>
      )}
      {!heroTitleAlwaysOnTop && (
        <>
          <div className="ctaSection__secondary-contents">{secondaryContents}</div>
          <HeaderTag className={headerClasses}>{title}</HeaderTag>
          {caption && <div className={copyClasses}>{caption}</div>}
        </>
      )}
      {text}
      <div className={buttonClasses}>{buttons}</div>
    </Component>
  );
};

TabletCallToActionSection.propTypes = {
  borderBottom: PropTypes.bool,
  heroTitleAlwaysOnTop: PropTypes.bool,
  caption: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  backgroundColor: PropTypes.oneOf([
    '',
    'white',
    'lightBlue',
    'softPink',
    'lightGray',
    'babyBlue',
    'registryLightBlue',
    'lightGreen',
    'hummingBird',
  ]),
  backgroundImage: PropTypes.string,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  secondaryContents: PropTypes.node,
  component: PropTypes.oneOf(['div', 'section']),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  buttons: PropTypes.node,
  headerTag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

export const CallToActionSection = ({
  component: Component = 'section',
  backgroundImage,
  mobileBackgroundImage,
  tabletBackgroundImage,
  className,
  buttons,
  title,
  headerTag: HeaderTag = 'h1',
  caption,
  text,
  smallerTitle = false,
  backgroundColor,
  backgroundChips,
  secondaryContents,
  secondaryRight = false,
  height,
  borderBottomOnMobile = false,
  heroTitleAlwaysOnTop,
}) => {
  const headerTagClasses = cx('ctaSection__title', `zui__${HeaderTag}`, {
    'ctaSection__title--smaller': smallerTitle,
  });
  const ctaSectionClasses = cx(
    'ctaSection',
    { 'ctaSection--light-blue-bg': backgroundColor === 'lightBlue' },
    { 'ctaSection--soft-pink-bg': backgroundColor === 'softPink' },
    { 'ctaSection--light-gray-bg': backgroundColor === 'lightGray' },
    { 'ctaSection--baby-blue-bg': backgroundColor === 'babyBlue' },
    { 'ctaSection--registry-light-blue-bg': backgroundColor === 'registryLightBlue' },
    { 'ctaSection--light-green-bg': backgroundColor === 'lightGreen' },
    { 'ctaSection--humming-bird-bg': backgroundColor === 'hummingBird' },
    className
  );
  const elementStyle = backgroundImage ? { backgroundImage, height } : { height };
  const renderPrimarySection = () => {
    return (
      <div className="ctaSection__primary">
        {title && HeaderTag && <HeaderTag className={headerTagClasses}>{title}</HeaderTag>}
        {caption && <div className="body-copy body-copy--light-gray">{caption}</div>}
        {text && <div className="ctaSection__text">{text}</div>}
        {buttons && <div className="ctaSection__buttons">{buttons}</div>}
      </div>
    );
  };

  return (
    <>
      <MobileCallToActionSection
        backgroundImage={mobileBackgroundImage || backgroundImage}
        backgroundColor={backgroundColor}
        buttons={buttons}
        caption={caption}
        headerTag={HeaderTag}
        secondaryContents={secondaryContents}
        title={title}
        borderBottom={borderBottomOnMobile}
        text={text}
        className={className}
      />
      <TabletCallToActionSection
        backgroundImage={tabletBackgroundImage || backgroundImage}
        backgroundColor={backgroundColor}
        buttons={buttons}
        caption={caption}
        text={text}
        headerTag={HeaderTag}
        secondaryContents={secondaryContents}
        title={title}
        className={className}
        heroTitleAlwaysOnTop={heroTitleAlwaysOnTop}
      />
      <Component className={ctaSectionClasses} style={elementStyle}>
        {backgroundChips}
        {secondaryRight ? (
          <div className="ctaSection__contents">
            {renderPrimarySection()}
            {secondaryContents && <div className="ctaSection__secondary">{secondaryContents}</div>}
          </div>
        ) : (
          <div className="ctaSection__contents">
            {secondaryContents && <div className="ctaSection__secondary">{secondaryContents}</div>}
            {renderPrimarySection()}
          </div>
        )}
      </Component>
    </>
  );
};

CallToActionSection.propTypes = {
  height: PropTypes.string,
  backgroundImage: PropTypes.string,
  mobileBackgroundImage: PropTypes.string,
  tabletBackgroundImage: PropTypes.string,
  className: PropTypes.string,
  backgroundChips: PropTypes.node,
  buttons: PropTypes.node,
  secondaryContents: PropTypes.node,
  smallerTitle: PropTypes.bool,
  title: PropTypes.node,
  caption: PropTypes.node,
  text: PropTypes.node,
  secondaryRight: PropTypes.bool,
  backgroundColor: PropTypes.oneOf([
    '',
    'white',
    'lightBlue',
    'softPink',
    'lightGray',
    'babyBlue',
    'registryLightBlue',
    'lightGreen',
    'hummingBird',
  ]),
  headerTag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  component: PropTypes.oneOf(['div', 'section']),
  borderBottomOnMobile: PropTypes.bool,
  heroTitleAlwaysOnTop: PropTypes.bool,
};

export default CallToActionSection;
